import { QuestionMarkCircleIcon } from "@heroicons/react/outline";
import { SparklesIcon } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useCallback, useMemo } from "react";

import blocks, { BlockType } from "../../..";

import { AssertionVariableToken, MultipleAssertions } from "./MultipleAssertions";

import { BlockProps } from "@/components/PowerBlock/Block";
import { useBlock, withBlockStore } from "@/components/PowerBlock/Block/block-store";
import InputVariablesField from "@/components/PowerBlock/Block/InputVariablesField";
import { useIsReadonly } from "@/components/PowerBlock/Block/readonly-context";
import SectionGroup from "@/components/PowerBlock/Block/SectionGroup";
import { SingleSourceSelector } from "@/components/PowerBlock/Block/SourceSelectors";
import ValidatedField from "@/components/PowerBlock/Block/ValidatedField";
import { conditional, required } from "@/components/PowerBlock/Block/validation/rules";
import { createBlockSchema } from "@/components/PowerBlock/Block/validation/schemaBuilder";
import { Toggle } from "@/components/Toggle";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { cn } from "@/lib/utils";
import { LLMAssertionConfiguration } from "@/types/evaluate";

/**
 * Parse f-string style variables from a template string.
 * Matches {variable_name} patterns (single curly braces).
 */
const parseVariablesFromTemplate = (template: string): string[] => {
  const variables = new Set<string>();
  const regex = /\{(\w+)\}/g;
  let match;
  while ((match = regex.exec(template)) !== null) {
    variables.add(match[1]);
  }
  return Array.from(variables);
};

/**
 * Extract all unique variables from assertions (which may be JSON array or single string)
 */
const extractVariablesFromAssertions = (prompt: string | undefined): string[] => {
  if (!prompt) {
    return [];
  }

  try {
    const parsed = JSON.parse(prompt);
    if (Array.isArray(parsed)) {
      const allVars = new Set<string>();
      for (const assertion of parsed) {
        for (const v of parseVariablesFromTemplate(String(assertion))) {
          allVars.add(v);
        }
      }
      return Array.from(allVars);
    }
  } catch {
    // Not JSON, treat as single string
  }

  return parseVariablesFromTemplate(prompt);
};

const validationSchema = createBlockSchema<LLMAssertionConfiguration>()
  .requireName()
  .addRule(required("configuration.source", "Source is required"))
  .addRule(
    conditional(
      "configuration.prompt_source",
      payload => payload.configuration?.prompt_source !== undefined,
      required("configuration.prompt_source", "Prompt source is required"),
    ),
  )
  .addRule(
    conditional(
      "configuration.prompt",
      payload => payload.configuration?.prompt_source === undefined,
      required("configuration.prompt", "At least one assertion is required"),
    ),
  )
  .build();

const LLMAssertionBlockCore = observer(
  ({ sources }: Omit<BlockProps<LLMAssertionConfiguration>, "DefaultFields">) => {
    const block = useBlock<LLMAssertionConfiguration>();
    const isReadonly = useIsReadonly();

    const isImportMode = block.configuration?.prompt_source !== undefined;

    const handleModeChange = useCallback(
      (importMode: boolean) => {
        if (importMode) {
          block.setConfiguration({
            ...block.configuration,
            prompt: undefined,
            prompt_source: "",
            source: block.configuration?.source || "",
            variable_mappings: undefined,
          });
        } else {
          block.setConfiguration({
            ...block.configuration,
            prompt: '[""]',
            prompt_source: undefined,
            source: block.configuration?.source || "",
            variable_mappings: undefined,
          });
        }
      },
      [block],
    );

    const handlePromptChange = useCallback(
      (prompt: string) => {
        block.setConfiguration({
          ...block.configuration,
          prompt,
          source: block.configuration?.source || "",
          prompt_source: undefined,
          variable_mappings: block.configuration?.variable_mappings,
        });
      },
      [block],
    );

    const assertions = useMemo(() => {
      const prompt: unknown = block.configuration?.prompt;
      if (!prompt) {
        return [""];
      }

      if (Array.isArray(prompt)) {
        return prompt.map(assertion => String(assertion));
      }

      const promptString = String(prompt);
      try {
        const parsed = promptString.startsWith("[") ? JSON.parse(promptString) : null;
        return Array.isArray(parsed) ? parsed.map(assertion => String(assertion)) : [promptString];
      } catch {
        return [promptString];
      }
    }, [block.configuration?.prompt]);

    const handleAssertionsChange = useCallback(
      (newAssertions: string[]) => {
        handlePromptChange(JSON.stringify(newAssertions));
      },
      [handlePromptChange],
    );

    const detectedVariables = useMemo(() => {
      if (block.configuration?.prompt_source !== undefined) {
        return [];
      }
      return extractVariablesFromAssertions(block.configuration?.prompt);
    }, [block.configuration?.prompt, block.configuration?.prompt_source]);

    const handleAutofill = useCallback(
      (mappings: Record<string, string>) => {
        if (block.configuration) {
          block.setConfiguration({
            ...block.configuration,
            variable_mappings: {
              ...block.configuration.variable_mappings,
              ...mappings,
            },
          });
        }
      },
      [block],
    );

    const AssertionIcon = useMemo(() => blocks[BlockType.LLM_ASSERTION].icon!, []);

    return (
      <>
        <SectionGroup
          title="Assertion"
          description="Define what the AI should check for"
          icon={<SparklesIcon className="h-4 w-4" />}
        >
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-1">
              <span className="text-sm text-gray-700">Import from Column</span>
              <BasicTooltip content="Enable to import assertions from a dataset column instead of writing them directly">
                <QuestionMarkCircleIcon className="h-4 w-4 text-gray-400" />
              </BasicTooltip>
            </div>
            <Toggle enabled={isImportMode} setEnabled={handleModeChange} disabled={isReadonly} />
          </div>

          <div className="grid">
            <div className={cn("col-start-1 row-start-1 space-y-4", isImportMode && "invisible")}>
              <div className="text-xs">
                Write your assertion below. You can optionally use{" "}
                <AssertionVariableToken name="column" /> to insert dataset values.
              </div>

              <ValidatedField name="Assertion" path="configuration.prompt">
                <MultipleAssertions
                  assertions={assertions}
                  assertionVariables={detectedVariables}
                  onAssertionsChange={handleAssertionsChange}
                />
              </ValidatedField>

              {detectedVariables.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-gray-700">Map variables to columns</p>
                  <InputVariablesField
                    variables={detectedVariables}
                    sources={sources!}
                    sourceKeyPrefix="variable_mappings"
                    showAutofill
                    onAutofill={handleAutofill}
                    templateFormat="f-string"
                  />
                </div>
              )}
            </div>

            <div className={cn("col-start-1 row-start-1 space-y-4", !isImportMode && "invisible")}>
              <div className="text-xs">Import assertions from a dataset column.</div>

              <SingleSourceSelector
                sources={sources!}
                sourceKey="prompt_source"
                validationPath="configuration.prompt_source"
                name="Assertion Source"
              />
            </div>
          </div>
        </SectionGroup>

        <SectionGroup
          title="Data Source"
          description="Select the data to evaluate"
          icon={<AssertionIcon className="h-4 w-4" />}
        >
          <SingleSourceSelector
            sources={sources!}
            sourceKey="source"
            validationPath="configuration.source"
            name="Source"
          />
        </SectionGroup>
      </>
    );
  },
);

export default withBlockStore<LLMAssertionConfiguration>(LLMAssertionBlockCore, {
  validationSchema,
});
